import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Copy, Check, CreditCard, User, DollarSign, Link2, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CreatePaymentLinkDialog({ open, onClose, customers = [] }) {
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [includeCCFee, setIncludeCCFee] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const parsedAmount = parseFloat(amount) || 0;
  const chargeAmount = includeCCFee ? Math.floor(parsedAmount * 1.03 * 100) / 100 : parsedAmount;
  const ccFeeAmount = Math.floor((chargeAmount - parsedAmount) * 100) / 100;

  const handleCustomerSelect = (e) => {
    const id = e.target.value;
    setSelectedCustomerId(id);
    if (id) {
      const c = customers.find((c) => c.id === id);
      if (c) {
        setCustomerName(c.name || "");
        setCustomerEmail(c.email || "");
        setCustomerPhone(c.phone || "");
      }
    }
  };

  const handleSubmit = async () => {
    if (!customerName.trim()) { toast.error("Customer name is required"); return; }
    if (!parsedAmount || parsedAmount <= 0) { toast.error("Enter a valid amount"); return; }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/payment-links/create`, {
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        amount: parsedAmount,
        include_cc_fee: includeCCFee,
        description: description || "One-Time Payment",
        origin_url: window.location.origin,
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create payment link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
    } catch {
      const el = document.createElement("textarea");
      el.value = result.url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Payment link copied!");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleClose = () => {
    setResult(null);
    setAmount("");
    setDescription("");
    setCustomerName("");
    setCustomerEmail("");
    setCustomerPhone("");
    setSelectedCustomerId("");
    setMode("existing");
    setIncludeCCFee(true);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#00341d]" />
            Create Payment Link
          </DialogTitle>
          <DialogDescription>
            Generate a one-time Stripe checkout link to share with any customer.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          /* ── Success State ── */
          <div className="space-y-5 pt-1">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <CreditCard className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="font-semibold text-green-900">Payment Link Created</p>
              <p className="text-sm text-green-700 mt-0.5">
                {result.include_cc_fee
                  ? `Customer pays $${result.charge_amount.toFixed(2)} (incl. 3% fee)`
                  : `Customer pays $${result.charge_amount.toFixed(2)}`}
              </p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3">
              <p className="text-xs text-zinc-500 uppercase tracking-wide font-medium mb-2">Payment Link</p>
              <p className="text-xs font-mono text-zinc-700 break-all mb-3 bg-white border border-zinc-200 rounded px-2 py-2">
                {result.url}
              </p>
              <Button
                onClick={handleCopy}
                className="w-full gap-2 bg-[#00341d] hover:bg-[#002816] text-white"
                data-testid="copy-payment-link-btn"
              >
                {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy Link</>}
              </Button>
            </div>

            <Button variant="outline" className="w-full" onClick={handleClose}>
              Done
            </Button>
          </div>
        ) : (
          /* ── Form State ── */
          <div className="space-y-4 pt-1">
            {/* Customer mode toggle */}
            <div className="flex rounded-lg border border-zinc-200 overflow-hidden text-sm">
              <button
                onClick={() => setMode("existing")}
                className={`flex-1 py-2 font-medium transition-colors ${mode === "existing" ? "bg-[#00341d] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="customer-mode-existing"
              >
                Existing Customer
              </button>
              <button
                onClick={() => setMode("new")}
                className={`flex-1 py-2 font-medium transition-colors ${mode === "new" ? "bg-[#00341d] text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                data-testid="customer-mode-new"
              >
                Enter Manually
              </button>
            </div>

            {/* Customer fields */}
            {mode === "existing" && customers.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-zinc-500 uppercase tracking-wide">Customer</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <select
                    className="w-full pl-9 pr-8 py-2 border border-zinc-200 rounded-md text-sm bg-white appearance-none focus:outline-none focus:ring-2 focus:ring-[#00341d]/20"
                    value={selectedCustomerId}
                    onChange={handleCustomerSelect}
                    data-testid="customer-select"
                  >
                    <option value="">— Select customer —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400 pointer-events-none" />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="plink-name" className="text-xs text-zinc-500 uppercase tracking-wide">Name *</Label>
                <Input id="plink-name" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="John Smith" data-testid="plink-customer-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="plink-phone" className="text-xs text-zinc-500 uppercase tracking-wide">Phone</Label>
                <Input id="plink-phone" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="(555) 000-0000" data-testid="plink-customer-phone" />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="plink-email" className="text-xs text-zinc-500 uppercase tracking-wide">Email</Label>
              <Input id="plink-email" type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="john@example.com" data-testid="plink-customer-email" />
            </div>

            <div className="space-y-1">
              <Label htmlFor="plink-desc" className="text-xs text-zinc-500 uppercase tracking-wide">Description</Label>
              <Input id="plink-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Tree removal deposit" data-testid="plink-description" />
            </div>

            {/* Amount + CC fee toggle */}
            <div className="space-y-1">
              <Label htmlFor="plink-amount" className="text-xs text-zinc-500 uppercase tracking-wide">Amount *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  id="plink-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  className="pl-9"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="plink-amount"
                />
              </div>
            </div>

            {/* CC fee toggle */}
            <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded-lg px-4 py-3">
              <div>
                <p className="text-sm font-medium text-zinc-800">Pass 3% CC fee to customer</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {includeCCFee && parsedAmount > 0
                    ? `Customer pays $${chargeAmount.toFixed(2)} (+$${ccFeeAmount.toFixed(2)} fee)`
                    : parsedAmount > 0
                    ? `You absorb the fee — customer pays $${parsedAmount.toFixed(2)}`
                    : "Toggle to choose who pays the card fee"}
                </p>
              </div>
              <button
                role="switch"
                aria-checked={includeCCFee}
                onClick={() => setIncludeCCFee((v) => !v)}
                className={`relative w-11 h-6 rounded-full transition-colors ${includeCCFee ? "bg-[#00341d]" : "bg-zinc-300"}`}
                data-testid="plink-cc-fee-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${includeCCFee ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>

            {/* Summary */}
            {parsedAmount > 0 && (
              <div className="bg-[#00341d]/5 border border-[#00341d]/20 rounded-lg px-4 py-3 text-sm">
                <div className="flex justify-between text-zinc-700">
                  <span>Base amount</span>
                  <span>${parsedAmount.toFixed(2)}</span>
                </div>
                {includeCCFee && (
                  <div className="flex justify-between text-zinc-500 text-xs mt-1">
                    <span>3% card processing fee</span>
                    <span>+${ccFeeAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-[#00341d] mt-2 pt-2 border-t border-[#00341d]/10">
                  <span>Customer pays</span>
                  <span>${chargeAmount.toFixed(2)}</span>
                </div>
              </div>
            )}

            <Button
              onClick={handleSubmit}
              disabled={loading || !customerName.trim() || !parsedAmount}
              className="w-full bg-[#00341d] hover:bg-[#002816] text-white font-bold h-11"
              data-testid="plink-submit-btn"
            >
              {loading ? "Creating..." : "Generate Payment Link"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
